import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { randomUUID } from 'node:crypto'

// Server-side admin client. Bypasses RLS. Used for all writes
// (anon submissions, message append, status changes) so the server
// can enforce business rules without RLS getting in the way.
function getAdminClient(): SupabaseClient {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  const key = process.env.SUPABASE_SERVICE_KEY || process.env.SUPABASE_SERVICE_ROLE_KEY
  if (!url || !key) {
    throw new Error('Missing Supabase admin credentials for support service')
  }
  return createClient(url, key, {
    auth: { autoRefreshToken: false, persistSession: false },
  })
}

export type SupportTicketApp =
  | 'CLIENT'
  | 'THERAPIST'
  | 'BETWEEN_SESSIONS'
  | 'EMPLOYEE'
  | 'EMPLOYER'
  | 'BROKER'
  | 'IMWELL'
  | 'IMWELL_STUDENTS'
  | 'IMWELL_CLINICIAN'
  | 'IMWELL_ADMIN'
  | 'RESOLVE'

export type SupportTicketCategory =
  | 'ACCOUNT'
  | 'BILLING'
  | 'BOOKING'
  | 'TECHNICAL'
  | 'QUESTION'
  | 'FEEDBACK'
  | 'OTHER'

export type SupportTicketStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'WAITING_ON_USER'
  | 'RESOLVED'
  | 'CLOSED'

export type SupportTicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'

export type SupportMessageRole = 'SUBMITTER' | 'ADMIN' | 'SYSTEM'
export type SupportMessageVisibility = 'PUBLIC' | 'INTERNAL'

export interface SupportAttachment {
  path: string
  name: string
  type: string
}

export interface SupportMessage {
  id: string
  role: SupportMessageRole
  visibility: SupportMessageVisibility
  authorUserId?: string | null
  authorProId?: string | null
  authorEmail?: string | null
  authorName?: string | null
  body: string
  attachments: SupportAttachment[]
  createdAt: string
  readBySubmitterAt?: string | null
  readByAdminAt?: string | null
}

export interface CreateSupportTicketInput {
  sourceApp: SupportTicketApp
  category: SupportTicketCategory
  customCategory?: string | null
  subject: string
  body: string
  // Submitter — one of these branches must be filled
  userId?: string | null
  proId?: string | null
  submitterEmail?: string | null
  submitterName?: string | null
  // Pre-populated / user-picked context
  episodeId?: string | null
  groupId?: string | null
  sessionId?: string | null
  companyId?: string | null
  // Debug bag (browser auto-fills url/userAgent/etc., API route adds ip)
  context?: Record<string, unknown>
  // Initial attachments uploaded at submit time
  attachments?: SupportAttachment[]
  priority?: SupportTicketPriority
}

export interface CreatedSupportTicket {
  id: string
  ticketNumber: number
  accessToken: string
  createdAt: string
}

const RATE_LIMIT_PER_HOUR = 3

/**
 * Anonymous-submission IP rate limit. Returns true if this IP may submit
 * one more ticket in the current hour, false if already at the cap.
 *
 * Counts anon tickets only (userId IS NULL AND proId IS NULL) within the
 * last hour where context->>'ip' matches. Logged-in submissions are not
 * subject to this limit.
 */
export async function checkAnonRateLimit(ip: string): Promise<{
  ok: boolean
  remaining: number
}> {
  if (!ip) return { ok: true, remaining: RATE_LIMIT_PER_HOUR }

  const supabase = getAdminClient()
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString()

  const { count, error } = await supabase
    .from('support_tickets')
    .select('id', { count: 'exact', head: true })
    .is('userId', null)
    .is('proId', null)
    .eq('context->>ip', ip)
    .gte('createdAt', oneHourAgo)

  if (error) {
    // Fail open on rate-limit lookup errors. Better to accept a possible
    // dup than to block a legit user because of an infra blip.
    return { ok: true, remaining: RATE_LIMIT_PER_HOUR }
  }

  const used = count ?? 0
  return { ok: used < RATE_LIMIT_PER_HOUR, remaining: Math.max(0, RATE_LIMIT_PER_HOUR - used) }
}

/**
 * Insert a new support ticket. Server-side only (uses service role).
 * Caller is responsible for any auth/rate-limit checks.
 */
export async function createSupportTicket(
  input: CreateSupportTicketInput
): Promise<CreatedSupportTicket> {
  const supabase = getAdminClient()
  const now = new Date().toISOString()
  const id = randomUUID()

  const row = {
    id,
    sourceApp: input.sourceApp,
    category: input.category,
    customCategory: input.customCategory ?? null,
    subject: input.subject,
    body: input.body,
    userId: input.userId ?? null,
    proId: input.proId ?? null,
    submitterEmail: input.submitterEmail ?? null,
    submitterName: input.submitterName ?? null,
    episodeId: input.episodeId ?? null,
    groupId: input.groupId ?? null,
    sessionId: input.sessionId ?? null,
    companyId: input.companyId ?? null,
    context: input.context ?? {},
    attachments: input.attachments ?? [],
    messages: [],
    priority: input.priority ?? 'MEDIUM',
    createdAt: now,
    updatedAt: now,
  }

  const { data, error } = await supabase
    .from('support_tickets')
    .insert(row)
    .select('id, ticketNumber, accessToken, createdAt')
    .single()

  if (error || !data) {
    throw new Error(`Failed to create support ticket: ${error?.message ?? 'unknown error'}`)
  }

  return data as CreatedSupportTicket
}

/**
 * Atomically append a message to a ticket's `messages` JSONB array via
 * Postgres `||` concatenation. Avoids the read-modify-write race that
 * would happen with two admins replying simultaneously.
 *
 * Goes through a Postgres RPC because the supabase-js client can't
 * express `messages = messages || $new::jsonb` natively.
 */
export async function appendSupportMessage(
  ticketId: string,
  message: Omit<SupportMessage, 'id' | 'createdAt'> & { id?: string; createdAt?: string }
): Promise<SupportMessage> {
  const supabase = getAdminClient()
  const now = new Date().toISOString()
  const full: SupportMessage = {
    id: message.id ?? randomUUID(),
    role: message.role,
    visibility: message.visibility,
    authorUserId: message.authorUserId ?? null,
    authorProId: message.authorProId ?? null,
    authorEmail: message.authorEmail ?? null,
    authorName: message.authorName ?? null,
    body: message.body,
    attachments: message.attachments ?? [],
    createdAt: message.createdAt ?? now,
    readBySubmitterAt: message.readBySubmitterAt ?? null,
    readByAdminAt: message.readByAdminAt ?? null,
  }

  const { error } = await supabase.rpc('append_support_ticket_message', {
    p_ticket_id: ticketId,
    p_message: full,
  })

  if (error) {
    throw new Error(`Failed to append support message: ${error.message}`)
  }

  return full
}

/**
 * Look up a ticket by its public access token (the anon-follow-up
 * magic-link from notification emails). Returns the ticket with
 * INTERNAL messages stripped — only the submitter view.
 */
export async function getSupportTicketByToken(token: string) {
  const supabase = getAdminClient()
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('accessToken', token)
    .single()

  if (error || !data) return null

  const messages = Array.isArray(data.messages) ? data.messages : []
  return {
    ...data,
    messages: messages.filter((m: SupportMessage) => m.visibility === 'PUBLIC'),
  }
}

/**
 * Strip INTERNAL messages from a ticket payload before returning it
 * to a submitter-facing endpoint. RLS can't filter inside a JSONB
 * array, so this happens in code.
 */
export function stripInternalMessages<T extends { messages?: unknown }>(ticket: T): T {
  const messages = Array.isArray(ticket.messages) ? (ticket.messages as SupportMessage[]) : []
  return {
    ...ticket,
    messages: messages.filter((m) => m.visibility === 'PUBLIC'),
  }
}

export const supportService = {
  checkAnonRateLimit,
  createSupportTicket,
  appendSupportMessage,
  getSupportTicketByToken,
  stripInternalMessages,
}
